/**
 * Trigger marks — the small circles a forecast lane draws for its triggers, and their legend.
 *
 * One vocabulary, each state readable on its own:
 *
 *   Trigger (live)          solid ring; the fill's strength is the trigger's chance
 *   Assumed in a scenario   dashed ring; filled when the scenario assumes it happens,
 *                           hollow when it assumes it doesn't
 *   Happened                filled, with a check
 *   Didn't happen           hollow, with a cross
 *   Not known               a question mark: on a dashed ring while a ruling may still come,
 *                           on a solid one when it could not be determined
 *
 * A scenario's assumption keeps its fill once the world rules: a filled mark with a cross reads
 * "assumed it would happen; it didn't". The ring turns solid when the trigger settles.
 *
 * Every mark is drawn centred in a `size` box (the hit area and layout the lane already uses),
 * at its own smaller `markSize`, so the lane's geometry does not change with the look.
 * Colours go through `style`, so both raw hex and the theme's `var(--c-*)` tokens work.
 */
import { PieIcon } from "./Icons.jsx";

const strokeStyle = (color) => ({ stroke: color });
const fillStyle = (color) => ({ fill: color });

function Check({ c, g, color, width }) {
  return (
    <polyline
      points={`${c - g},${c} ${c - g * 0.3},${c + g * 0.72} ${c + g},${c - g * 0.72}`}
      fill="none"
      style={strokeStyle(color)}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

function Question({ c, g, color, width }) {
  // A hook over a short stem, then a dot, filling the same ±g box as the check and cross.
  const R = g * 0.55;
  const hy = c - g * 0.45;
  const d = R * Math.SQRT1_2;
  return (
    <g style={strokeStyle(color)} strokeWidth={width} strokeLinecap="round" fill="none">
      <path
        d={`M ${c - R} ${hy} A ${R} ${R} 0 1 1 ${c + d} ${hy + d} Q ${c} ${c + g * 0.08} ${c} ${c + g * 0.35}`}
      />
      <circle cx={c} cy={c + g * 0.85} r={width * 0.55} style={fillStyle(color)} stroke="none" />
    </g>
  );
}

function Cross({ c, g, color, width }) {
  return (
    <g style={strokeStyle(color)} strokeWidth={width} strokeLinecap="round">
      <line x1={c - g} y1={c - g} x2={c + g} y2={c + g} />
      <line x1={c - g} y1={c + g} x2={c + g} y2={c - g} />
    </g>
  );
}

/**
 * A live trigger: a solid ring whose fill strength is its chance (`null` → hollow).
 *
 * @param {object} props
 * @param {number} props.size       box (hit area) in px
 * @param {number} [props.markSize] visible diameter, default 12
 * @param {number|null} props.chance 0–1
 * @param {string} props.color      ring and fill colour
 * @param {string} props.bg         ground behind the mark (hides the spine under it)
 */
export function TriggerMark({ size, markSize = 12, chance = null, color, bg }) {
  const c = size / 2;
  const r = markSize / 2 - 0.75;
  const p =
    typeof chance === "number" && Number.isFinite(chance) ? Math.max(0, Math.min(1, chance)) : null;
  return (
    <svg
      width={size}
      height={size}
      style={{ display: "block", overflow: "visible" }}
      aria-label={p == null ? "trigger" : `trigger, ${Math.round(p * 100)}% chance`}
    >
      <circle cx={c} cy={c} r={r} style={fillStyle(bg)} />
      {p != null && (
        <circle cx={c} cy={c} r={r} style={fillStyle(color)} fillOpacity={0.1 + 0.8 * p} />
      )}
      <circle cx={c} cy={c} r={r} fill="none" style={strokeStyle(color)} strokeWidth={1.5} />
    </svg>
  );
}

/**
 * A scenario's trigger, or any settled trigger.
 *
 * @param {object}   props
 * @param {number}   props.size           box in px
 * @param {number}   [props.markSize]     visible diameter (defaults to the box)
 * @param {boolean}  props.assertedOccurs the scenario assumes it happens → filled
 * @param {boolean}  props.settled        the world has ruled → solid ring + check/cross
 * @param {boolean}  props.watching       its window is open and a verdict is due (longer dash)
 * @param {boolean}  props.occurred       what happened (read only when settled)
 * @param {boolean}  [props.unknown]      what happened is not known: a "?" in place of the
 *                                        check or cross; with `settled`, it never will be
 * @param {string}   [props.label]        the accessible name, when the default does not fit
 * @param {string}   props.color          ring/fill colour
 * @param {string}   props.bg             ground behind the mark
 * @param {string}   props.glyphOnFill    check/cross colour over a filled mark
 */
export default function TriggerNode({
  size,
  markSize,
  assertedOccurs,
  settled = false,
  watching = false,
  occurred = false,
  unknown = false,
  color,
  bg,
  glyphOnFill = "#ffffff",
  label,
}) {
  const m = markSize ?? size;
  const c = size / 2;
  const r = m / 2 - 0.75;
  const filled = !!assertedOccurs;
  const glyph = filled ? glyphOnFill : color;
  const g = r * 0.48;
  const gw = Math.max(1.4, m * 0.11);
  const dash = settled ? undefined : watching ? "3.4 1.6" : "2 1.8";
  const assumed = assertedOccurs ? "assumed to happen" : "assumed not to happen";
  return (
    <svg
      width={size}
      height={size}
      style={{ display: "block", overflow: "visible" }}
      aria-label={
        label ??
        (unknown && settled
          ? "Sayer couldn't tell whether the trigger happened"
          : settled
            ? occurred
              ? "trigger happened"
              : "trigger did not happen"
            : `${assumed}${unknown ? ", its dates passed with no ruling yet" : watching ? ", awaiting the outcome" : ""}`)
      }
    >
      <circle cx={c} cy={c} r={r} style={fillStyle(bg)} />
      {filled && <circle cx={c} cy={c} r={r} style={fillStyle(color)} fillOpacity={0.9} />}
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        style={strokeStyle(color)}
        strokeWidth={1.5}
        strokeDasharray={dash}
      />
      {unknown ? (
        <Question c={c} g={g} color={glyph} width={gw} />
      ) : settled ? (
        occurred ? (
          <Check c={c} g={g} color={glyph} width={gw} />
        ) : (
          <Cross c={c} g={g * 0.85} color={glyph} width={gw} />
        )
      ) : null}
    </svg>
  );
}

/** A past trigger: filled with a check when it happened, hollow with a cross when it didn't. */
export function SettledMark({ size, markSize = 14, occurred, color, bg }) {
  return (
    <TriggerNode
      size={size}
      markSize={markSize}
      assertedOccurs={occurred}
      settled
      occurred={occurred}
      color={color}
      bg={bg}
      glyphOnFill={bg}
    />
  );
}

/**
 * A past trigger whose outcome is not known: hollow with a "?", on a dashed ring while a ruling
 * may still come and a solid one once it could not be determined (`final`).
 */
export function UnknownMark({ size, markSize = 14, final = false, color, bg }) {
  return (
    <TriggerNode
      size={size}
      markSize={markSize}
      assertedOccurs={false}
      settled={final}
      watching
      unknown
      color={color}
      bg={bg}
      label={final ? undefined : "no ruling yet on whether the trigger happened"}
    />
  );
}

/**
 * The one-line key to the lane's marks.
 *
 * @param {object} props
 * @param {string} props.color  the live-trigger colour
 * @param {string} props.muted  the settled-trigger colour
 * @param {string} props.bg     the lane ground
 * @param {string} props.text   label colour
 */
export function TriggerLegend({ color, muted, bg, text, style }) {
  const S = 14;
  const items = [
    [
      "Trigger: slice is the chance, size is the impact",
      <PieIcon key="t" p={0.35} sz={S + 2} color={color} bg={bg} />,
    ],
    [
      "Assumed in a scenario",
      <TriggerNode
        key="a"
        size={S}
        markSize={12}
        assertedOccurs
        color={color}
        bg={bg}
        glyphOnFill={bg}
      />,
    ],
    ["Happened", <SettledMark key="h" size={S} markSize={13} occurred color={muted} bg={bg} />],
    [
      "Didn't happen",
      <SettledMark key="d" size={S} markSize={13} occurred={false} color={muted} bg={bg} />,
    ],
  ];
  return (
    <div
      aria-label="Legend"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 16,
        rowGap: 4,
        fontSize: 12,
        lineHeight: "16px",
        color: text,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {items.map(([label, mark]) => (
        <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {mark}
          {label}
        </span>
      ))}
    </div>
  );
}
